import React, { cloneElement, useCallback, useMemo } from 'react';
import type { FunctionComponent, ReactElement } from 'react';

import { useLocation, useParams } from '../../../contexts/RouterContext';
import { getProjectBaseURLFromParams } from '../../../utils';
import SidebarNavLayout from '../../layouts/SidebarNavLayout';
import type { RevisionRouteParams } from '../projectDashboardTypes';

import { useInventoryFeatures } from './hooks/useInventoryFeatures';
import type { InventoryScope } from './types';

type InventoryPath = 'dependencies' | 'snippets' | 'binary' | 'vendored';

/**
 * ProjectInventoryBrowseContainer - When the Inventory UI FF is enabled, this will display the sidebar nav for
 * both the Dependencies, Snippets, and Binary Analysis (now grouped together as "Inventory").
 */
const ProjectInventoryBrowseContainer: FunctionComponent = React.memo(({ children, ...props }) => {
  const params = useParams<RevisionRouteParams>();
  const location = useLocation();

  const baseProjectURL = getProjectBaseURLFromParams(params);
  const isPathActive = useCallback((pathPart: InventoryPath) => {
    const pathWithoutTrailingSlash = location.pathname.replace(/\/$/, '');
    return pathWithoutTrailingSlash.endsWith(pathPart);
  }, [location.pathname]);

  const projectLocator = params.project_id;
  const inventoryScope = useMemo<InventoryScope>(() => ({
    scope: 'project',
    revision: projectLocator,
  }), [projectLocator]);

  const {
    shouldShowBinaryAnalysis,
    shouldShowSnippets,
    shouldShowVendored,
  } = useInventoryFeatures(inventoryScope);

  return (
    <SidebarNavLayout>
      <SidebarNavLayout.Nav>
        <SidebarNavLayout.NavHeading>Inventory</SidebarNavLayout.NavHeading>
        <SidebarNavLayout.NavItem
          to={`${baseProjectURL}/browse/dependencies`}
          isActive={isPathActive('dependencies')}
        >
          Dependencies
        </SidebarNavLayout.NavItem>
        {shouldShowSnippets && (
          <SidebarNavLayout.NavItem
            to={`${baseProjectURL}/browse/snippets`}
            isActive={isPathActive('snippets')}
            >
            Snippets
          </SidebarNavLayout.NavItem>
        )}
        {shouldShowBinaryAnalysis && (
          <SidebarNavLayout.NavItem
            to={`${baseProjectURL}/browse/binary`}
            isActive={isPathActive('binary')}
            >
            Binary Analysis
          </SidebarNavLayout.NavItem>
        )}
        {shouldShowVendored && (
          <SidebarNavLayout.NavItem
            to={`${baseProjectURL}/browse/vendored`}
            isActive={isPathActive('vendored')}
            >
            Vendored
          </SidebarNavLayout.NavItem>
        )}
      </SidebarNavLayout.Nav>
      <SidebarNavLayout.Content>
        {children && cloneElement(children as ReactElement, { ...props, scope: 'project' })}
      </SidebarNavLayout.Content>
    </SidebarNavLayout>
  );
});

export default ProjectInventoryBrowseContainer;
